import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import {
  useGetPaginatedCategoriesQuery,
  useLazyGetPaginatedCategoriesQuery,
  useCreateCategoryMutation,
} from "@/api/ticketApi/category";
import ExportMenu from "@/components/common/ExportMenu";
import { fetchAllPages } from "@/utils/listExport";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import CategoryListTable from "./CategoryListTable";
import CategoryFormDialog from "./CategoryFormDialog";
import { Category, QueryParams } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

export default function CategoryList() {
  const { t } = useTranslation(["common", "category"]);
  const user = useCurrentUser();
  
  // Deep link from the activity log: /categories?id=7 opens the list on that record.
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get("id");
  const [queryParams, setQueryParams] = useState<QueryParams>({
      filters: linkedId ? { category_id: linkedId } : {},
      pagination: {
          page: 1,
          pageSize: 15
      }
  });

  const { data: categoriesData, isLoading } = useGetPaginatedCategoriesQuery(queryParams);
  const [fetchAllCategories] = useLazyGetPaginatedCategoriesQuery();
  const [createCategory] = useCreateCategoryMutation();

  // The screen shows one page; a download is expected to hold everything the
  // filters match, so ask for the whole set rather than reusing the page data.
  const fetchExportRows = () =>
    fetchAllPages((page, pageSize) =>
      fetchAllCategories({
        filters: queryParams.filters,
        pagination: { page, pageSize },
      }).unwrap()
    );

  const exportFilters = Object.entries(queryParams.filters ?? {})
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => ({ label: t(`category:${k}`, { defaultValue: k }), value: String(v) }));
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: Category) => {
      createCategory(data)
        .unwrap()
        .then(() => {
            toast.success(t("category:categoryCreated"));
            setIsCreateModalOpen(false);
        })
        .catch(err => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
      setQueryParams(prev => ({
          ...prev,
          pagination: {
              ...prev.pagination,
              page
          }
      }));
  };
  
  const handleFilterChange = (filters: any) => {
    setQueryParams(prev => ({
        ...prev,
        filters,
        pagination: { ...prev.pagination, page: 1 } // Reset to page 1 on filter change
    }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
           <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("category:categories")}</Breadcrumb.Item>
            </Breadcrumb>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("category:categories")}
                </h1>
                <p className="text-gray-500">{t("category:manageCategories")}</p>
              </div>
              <div className="flex items-center gap-2">
                <ExportMenu
                  title={t("category:categories")}
                  filename="kategoriler"
                  filters={exportFilters}
                  fetchRows={fetchExportRows}
                  columns={[
                    { header: "ID", value: (r: any) => r.category_id ?? "" },
                    { header: t("category:categoryName"), value: (r: any) => r.category_name ?? "" },
                    { header: t("category:description"), value: (r: any) => r.category_description ?? "" },
                    { header: t("category:group"), value: (r: any) => r.group?.group_name ?? "" },
                    { header: t("category:priority"), value: (r: any) => r.priority?.priority_name ?? "" },
                    { header: t("common:status"), value: (r: any) => (r.is_active ? t("common:active") : t("common:inactive")) },
                  ]}
                />
                {isCompanyAdminUser(user) && (
                  <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                      <HiPlus className="mr-2 h-5 w-5" />
                      {t("category:createCategory")}
                  </Button>
                )}
              </div>
            </div>
           </div>
           
           <CategoryListTable
              data={categoriesData?.items || []}
              pagination={categoriesData?.pagination ? {
                  pageSize: categoriesData.pagination.pageSize || 15,
                  pageIndex: categoriesData.pagination.pageIndex ?? categoriesData.pagination.page ?? 1,
                  totalPages: categoriesData.pagination.totalPages ?? 1,
                  totalItems: categoriesData.pagination.totalItems ?? categoriesData.pagination.total ?? 0
              } : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }}
              onPageChange={handlePageChange}
              isLoading={isLoading}
              filters={queryParams.filters}
              onFilterChange={handleFilterChange}
           />
        </div>
      </div>
      
      <CategoryFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />
    </NavbarSidebarLayout>
  );
}
